import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { format, parseISO, eachDayOfInterval, isBefore, startOfDay } from 'date-fns';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import Header from '../components/Header';
import DeskGrid from '../components/DeskGrid';
import { Calendar } from '../components/ui/calendar';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import { Checkbox } from '../components/ui/checkbox';
import { CalendarDays, Loader2, Trash2, BookOpen, UserCheck, CalendarRange, ShoppingCart } from 'lucide-react';
import { groupConsecutiveBookings } from '../utils/groupBookings';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function UserDashboard() {
  const { isAdmin } = useAuth();
  const { t, dateLocale } = useLanguage();
  const [desks, setDesks] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [myBookings, setMyBookings] = useState([]);
  const [dateRange, setDateRange] = useState({ from: undefined, to: undefined });
  const [selectedDesk, setSelectedDesk] = useState(null);
  const [loading, setLoading] = useState(true);
  const [bookingLoading, setBookingLoading] = useState(false);
  const [allUsers, setAllUsers] = useState([]);
  const [onBehalfOf, setOnBehalfOf] = useState('');
  const [showLongPeriod, setShowLongPeriod] = useState(false);
  const [lpForm, setLpForm] = useState({ start_date: '', end_date: '', desk_id: '', notes: '' });
  const [lpSubmitting, setLpSubmitting] = useState(false);
  const [myLpRequests, setMyLpRequests] = useState([]);
  const [holidays, setHolidays] = useState([]);
  const [showPastBookings, setShowPastBookings] = useState(false);

  const isWeekendOrHoliday = useCallback((date) => {
    const day = date.getDay();
    if (day === 0 || day === 6) return true;
    const dateStr = format(date, 'yyyy-MM-dd');
    return holidays.includes(dateStr);
  }, [holidays]);

  const getSelectedDates = useCallback(() => {
    if (!dateRange.from) return [];
    if (!dateRange.to) return dateRange.from && !isWeekendOrHoliday(dateRange.from) ? [dateRange.from] : [];
    const allDays = eachDayOfInterval({ start: dateRange.from, end: dateRange.to });
    return allDays.filter(d => !isWeekendOrHoliday(d));
  }, [dateRange, isWeekendOrHoliday]);

  const fetchDesks = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/desks`, { withCredentials: true });
      setDesks(data);
    } catch {
      toast.error('Error');
    }
  }, []);

  const fetchMyBookings = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/bookings`, { withCredentials: true });
      setMyBookings(data);
    } catch {
      toast.error('Error');
    }
  }, []);

  const fetchBookingsForDates = useCallback(async (dates) => {
    if (dates.length === 0) {
      setBookings([]);
      return;
    }

    try {
      const dateStrings = dates.map(d => format(d, 'yyyy-MM-dd'));
      const { data } = await axios.get(
        `${API_URL}/api/bookings/by-dates?dates=${dateStrings.join(',')}`,
        { withCredentials: true }
      );
      setBookings(data);
    } catch {
      toast.error('Error');
    }
  }, []);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      const promises = [fetchDesks(), fetchMyBookings()];
      if (isAdmin) {
        promises.push(
          axios.get(`${API_URL}/api/admin/users`, { withCredentials: true })
            .then(({ data }) => setAllUsers(data))
            .catch(() => {})
        );
      }
      promises.push(
        axios.get(`${API_URL}/api/long-period-requests`, { withCredentials: true })
          .then(({ data }) => setMyLpRequests(data))
          .catch(() => {})
      );
      promises.push(
        axios.get(`${API_URL}/api/holidays`, { withCredentials: true })
          .then(({ data }) => setHolidays(data.holidays || []))
          .catch(() => {})
      );
      await Promise.all(promises);
      setLoading(false);
    };
    loadData();
  }, [fetchDesks, fetchMyBookings, isAdmin]);

  useEffect(() => {
    const dates = getSelectedDates();
    fetchBookingsForDates(dates);
    setSelectedDesk(null);
  }, [dateRange, fetchBookingsForDates, getSelectedDates]);

  const handleDateSelect = (range) => {
    setDateRange(range || { from: undefined, to: undefined });
  };

  const handleDeskClick = (deskId) => {
    setSelectedDesk(selectedDesk === deskId ? null : deskId);
  };

  const handleBooking = async () => {
    const selectedDates = getSelectedDates();
    if (!selectedDesk || selectedDates.length === 0) {
      toast.error(t('selectPeriodAndDesk'));
      return;
    }

    setBookingLoading(true);
    try {
      const dateStrings = selectedDates.map(d => format(d, 'yyyy-MM-dd'));

      if (isAdmin) {
        // Admin: direct booking
        const payload = { desk_id: selectedDesk, dates: dateStrings };
        if (onBehalfOf && onBehalfOf !== 'self') {
          payload.on_behalf_of = onBehalfOf;
        }
        await axios.post(`${API_URL}/api/bookings`, payload, { withCredentials: true });
        toast.success(t('deskBookedSuccess', { desk: selectedDesk }));
        await Promise.all([fetchMyBookings(), fetchBookingsForDates([])]);
      } else {
        // User: add to cart
        await axios.post(`${API_URL}/api/cart/add`, {
          item_type: 'desk',
          resource_id: String(selectedDesk),
          dates: dateStrings
        }, { withCredentials: true });
        toast.success(t('addedToCart'));
      }
      setSelectedDesk(null);
      setDateRange({ from: undefined, to: undefined });
      setOnBehalfOf('');
    } catch (e) {
      const errorMsg = e.response?.data?.detail || t('error');
      toast.error(errorMsg);
    } finally {
      setBookingLoading(false);
    }
  };

  const handleCancelBooking = async (bookingId) => {
    try {
      await axios.delete(`${API_URL}/api/bookings/${bookingId}`, { withCredentials: true });
      toast.success(t('bookingCancelled'));
      await fetchMyBookings();
    } catch (e) {
      toast.error(t('error'));
    }
  };

  const handleLongPeriodSubmit = async () => {
    if (!lpForm.start_date || !lpForm.end_date) return;
    setLpSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/long-period-requests`, {
        start_date: lpForm.start_date,
        end_date: lpForm.end_date,
        desk_id: lpForm.desk_id ? parseInt(lpForm.desk_id) : null,
        notes: lpForm.notes
      }, { withCredentials: true });
      toast.success(t('requestSent'));
      setShowLongPeriod(false);
      setLpForm({ start_date: '', end_date: '', desk_id: '', notes: '' });
      const { data } = await axios.get(`${API_URL}/api/long-period-requests`, { withCredentials: true });
      setMyLpRequests(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || t('error'));
    } finally {
      setLpSubmitting(false);
    }
  };

  const selectedDates = getSelectedDates();
  const today = startOfDay(new Date());

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Header />
        <div className="flex items-center justify-center h-[calc(100vh-100px)]">
          <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="user-dashboard">
      <Header />
      
      <main className="max-w-7xl mx-auto px-2 md:px-4 py-4 md:py-8">
        <Tabs defaultValue="book" className="space-y-4 md:space-y-6">
          <TabsList className="bg-white rounded-full p-1 shadow-sm w-full justify-center">
            <TabsTrigger 
              value="book" 
              data-testid="tab-book"
              className="rounded-full px-3 md:px-6 text-xs md:text-sm data-[state=active]:bg-blue-500 data-[state=active]:text-white"
            >
              <CalendarDays className="w-3 h-3 md:w-4 md:h-4 mr-1 md:mr-2" />
              {t('book')}
            </TabsTrigger>
            <TabsTrigger 
              value="my-bookings" 
              data-testid="tab-my-bookings"
              className="rounded-full px-3 md:px-6 text-xs md:text-sm data-[state=active]:bg-blue-500 data-[state=active]:text-white"
            >
              <BookOpen className="w-3 h-3 md:w-4 md:h-4 mr-1 md:mr-2" />
              <span className="hidden sm:inline">{t('myBookings')}</span>
              <span className="sm:hidden">{t('bookings')}</span>
            </TabsTrigger>
          </TabsList>

          <TabsContent value="book" className="space-y-4 md:space-y-6 animate-fade-in-up">
            <div className="grid lg:grid-cols-3 gap-4 md:gap-6">
              <Card className="card-floating lg:col-span-1">
                <CardHeader className="pb-2 md:pb-4">
                  <CardTitle className="text-lg md:text-xl font-extrabold text-slate-900">
                    {t('selectPeriod')}
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-3 md:p-6 pt-0">
                  <Calendar
                    mode="range"
                    selected={dateRange}
                    onSelect={handleDateSelect}
                    locale={dateLocale}
                    disabled={[{ before: today }, { dayOfWeek: [0, 6] }, ...holidays.map(h => new Date(h + 'T00:00:00'))]}
                    modifiers={{ holiday: holidays.map(h => new Date(h + 'T00:00:00')) }}
                    modifiersClassNames={{ holiday: 'rdp-day_disabled' }}
                    numberOfMonths={1}
                    className="rounded-xl"
                    data-testid="booking-calendar"
                  />
                  
                  {dateRange.from && (
                    <div className="mt-4 p-3 bg-blue-50 rounded-xl">
                      <p className="text-sm font-semibold text-blue-700">
                        {t('selectedPeriodLabel')}
                      </p>
                      <p className="text-sm text-blue-600 mt-1">
                        {format(dateRange.from, 'dd MMMM yyyy', { locale: dateLocale })}
                        {dateRange.to && dateRange.to !== dateRange.from && (
                          <> → {format(dateRange.to, 'dd MMMM yyyy', { locale: dateLocale })}</>
                        )}
                      </p>
                      <p className="text-xs text-blue-500 mt-1">
                        {selectedDates.length} {selectedDates.length === 1 ? t('day') : t('days')}
                      </p>
                    </div>
                  )}
                  
                  {!dateRange.from && (
                    <p className="mt-4 text-sm text-slate-500 text-center">
                      {t('clickStartEnd')}
                    </p>
                  )}

                  {/* Long period link */}
                  <div className="mt-4 text-center">
                    <button
                      onClick={() => setShowLongPeriod(true)}
                      data-testid="long-period-link"
                      className="text-xs text-blue-600 hover:text-blue-800 hover:underline inline-flex items-center gap-1"
                    >
                      <CalendarRange className="w-3 h-3" />
                      {t('longPeriodLink')} <span className="font-bold">{t('longPeriodClickHere')}</span>
                    </button>
                  </div>
                </CardContent>
              </Card>

              <Card className="card-floating lg:col-span-2">
                <CardHeader className="pb-2 md:pb-4">
                  <CardTitle className="text-lg md:text-xl font-extrabold text-slate-900">
                    {t('deskMap')}
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-3 md:p-6 pt-0">
                  <DeskGrid
                    desks={desks}
                    bookings={bookings}
                    selectedDesk={selectedDesk}
                    onDeskClick={handleDeskClick}
                    isAdmin={isAdmin}
                    selectedDates={selectedDates}
                  />
                  
                  {selectedDesk && selectedDates.length > 0 && (
                    <div className="mt-4 md:mt-6 p-3 md:p-4 bg-indigo-50 rounded-xl md:rounded-2xl space-y-3 animate-fade-in-up">
                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                        <div>
                          <p className="font-bold text-indigo-900 text-sm md:text-base">
                            {t('deskSelected', { desk: selectedDesk })}
                          </p>
                          <p className="text-xs md:text-sm text-indigo-600">
                            {t('from')} {format(dateRange.from, 'dd/MM/yyyy', { locale: dateLocale })}
                            {dateRange.to && dateRange.to !== dateRange.from && (
                              <> {t('to')} {format(dateRange.to, 'dd/MM/yyyy', { locale: dateLocale })}</>
                            )}
                            {' '}({selectedDates.length} {selectedDates.length === 1 ? t('day') : t('days')})
                          </p>
                        </div>
                        <Button
                          onClick={handleBooking}
                          disabled={bookingLoading}
                          data-testid="confirm-booking-btn"
                          className={`w-full sm:w-auto text-sm ${isAdmin ? 'btn-primary' : 'bg-emerald-500 hover:bg-emerald-600 text-white rounded-full px-6 font-bold'}`}
                        >
                          {bookingLoading ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : isAdmin ? (
                            t('confirm')
                          ) : (
                            <><ShoppingCart className="w-4 h-4 mr-1" />{t('addToCart')}</>
                          )}
                        </Button>
                      </div>
                      {isAdmin && allUsers.length > 0 && (
                        <div className="flex items-center gap-2 pt-2 border-t border-indigo-200">
                          <UserCheck className="w-4 h-4 text-indigo-600" />
                          <span className="text-xs font-semibold text-indigo-700">{t('bookOnBehalf')}:</span>
                          <Select value={onBehalfOf} onValueChange={setOnBehalfOf}>
                            <SelectTrigger className="flex-1 rounded-xl h-9 text-xs" data-testid="on-behalf-select">
                              <SelectValue placeholder={t('selectUser')} />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="self">-- {t('selectUser')} --</SelectItem>
                              {allUsers.filter(u => u.id !== undefined).map(u => (
                                <SelectItem key={u.id} value={u.id}>{u.name} ({u.email})</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="my-bookings" className="animate-fade-in-up">
            <Card className="card-floating">
              <CardHeader>
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <CardTitle className="text-xl font-extrabold text-slate-900">
                    {t('yourBookings')}
                  </CardTitle>
                  <div className="flex items-center gap-2">
                    <Checkbox
                      id="show-past-user"
                      checked={showPastBookings}
                      onCheckedChange={setShowPastBookings}
                      data-testid="show-past-bookings-checkbox"
                    />
                    <label htmlFor="show-past-user" className="text-sm text-slate-600 cursor-pointer select-none">
                      {t('showPastBookings')}
                    </label>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {myBookings.length === 0 ? (
                  <div className="text-center py-12">
                    <img 
                      src="https://images.unsplash.com/photo-1686563636216-02d5c3674816?crop=entropy&cs=srgb&fm=jpg&w=200&h=200&fit=crop"
                      alt=""
                      className="w-48 h-48 mx-auto mb-4 opacity-80 rounded-2xl"
                    />
                    <p className="text-slate-500 font-medium">
                      {t('noBookings')}
                    </p>
                    <p className="text-slate-400 text-sm mt-1">
                      {t('goToBook')}
                    </p>
                  </div>
                ) : (() => {
                  const grouped = groupConsecutiveBookings(myBookings);
                  const filtered = showPastBookings 
                    ? grouped 
                    : grouped.filter(g => !isBefore(parseISO(g.end_date), today));
                  const sorted = [...filtered].sort((a, b) => a.start_date.localeCompare(b.start_date));
                  
                  return sorted.length === 0 ? (
                    <p className="text-center text-slate-400 py-8">{t('noBookings')}</p>
                  ) : (
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                      {sorted.map((group) => {
                        const startDate = parseISO(group.start_date);
                        const endDate = parseISO(group.end_date);
                        const isPast = isBefore(endDate, today);
                        const isRange = group.start_date !== group.end_date;
                        
                        return (
                          <div
                            key={group.ids[0]}
                            data-testid={`booking-group-${group.ids[0]}`}
                            className={`rounded-2xl p-4 flex justify-between items-start ${
                              isPast 
                                ? 'bg-slate-100' 
                                : 'bg-gradient-to-br from-blue-50 to-indigo-50'
                            }`}
                          >
                            <div>
                              <p className={`font-bold ${isPast ? 'text-slate-500' : 'text-slate-900'}`}>
                                {t('desk')} {group.desk_id}
                              </p>
                              {isRange ? (
                                <div className={`text-sm ${isPast ? 'text-slate-400' : 'text-slate-600'}`}>
                                  <p>{t('from')}: {format(startDate, 'EEEE d MMMM yyyy', { locale: dateLocale })}</p>
                                  <p>{t('to')}: {format(endDate, 'EEEE d MMMM yyyy', { locale: dateLocale })}</p>
                                  <p className="text-xs mt-1 text-blue-500 font-semibold">{group.ids.length} {group.ids.length === 1 ? t('day') : t('days')}</p>
                                </div>
                              ) : (
                                <p className={`text-sm ${isPast ? 'text-slate-400' : 'text-slate-600'}`}>
                                  {format(startDate, 'EEEE d MMMM yyyy', { locale: dateLocale })}
                                </p>
                              )}
                              {isPast && (
                                <span className="text-xs text-slate-400">{t('past')}</span>
                              )}
                            </div>
                            {!isPast && (
                              <Button
                                variant="ghost"
                                size="icon"
                                data-testid={`cancel-booking-${group.ids[0]}`}
                                onClick={async () => {
                                  for (const id of group.ids) await handleCancelBooking(id);
                                }}
                                className="text-rose-500 hover:text-rose-700 hover:bg-rose-50 rounded-full"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  );
                })()}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>

      {/* Long Period Request Dialog */}
      <Dialog open={showLongPeriod} onOpenChange={setShowLongPeriod}>
        <DialogContent className="rounded-3xl max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-extrabold flex items-center gap-2">
              <CalendarRange className="w-5 h-5 text-blue-500" />
              {t('longPeriodRequest')}
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-500 mb-4">{t('longPeriodDesc')}</p>
          
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-xs font-semibold">{t('startDate')}</Label>
                <Input type="date" value={lpForm.start_date} data-testid="lp-start-date"
                  onChange={e => setLpForm(f => ({ ...f, start_date: e.target.value }))}
                  className="rounded-xl h-10 text-sm" required />
              </div>
              <div className="space-y-1">
                <Label className="text-xs font-semibold">{t('endDate')}</Label>
                <Input type="date" value={lpForm.end_date} data-testid="lp-end-date"
                  onChange={e => setLpForm(f => ({ ...f, end_date: e.target.value }))}
                  className="rounded-xl h-10 text-sm" required />
              </div>
            </div>
            <div className="space-y-1">
              <Label className="text-xs font-semibold">{t('preferredDesk')}</Label>
              <Select value={lpForm.desk_id} onValueChange={v => setLpForm(f => ({ ...f, desk_id: v === 'none' ? '' : v }))}>
                <SelectTrigger className="rounded-xl h-10 text-sm" data-testid="lp-desk-select">
                  <SelectValue placeholder={t('noPreference')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">{t('noPreference')}</SelectItem>
                  {desks.filter(d => d.is_available).map(d => (
                    <SelectItem key={d.desk_id} value={String(d.desk_id)}>{t('desk')} {d.desk_id}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs font-semibold">{t('notes')}</Label>
              <Textarea value={lpForm.notes} data-testid="lp-notes"
                onChange={e => setLpForm(f => ({ ...f, notes: e.target.value }))}
                placeholder={t('notesPlaceholder')} className="rounded-xl text-sm" rows={3} />
            </div>
            <div className="flex gap-3 pt-2">
              <Button variant="outline" onClick={() => setShowLongPeriod(false)} className="flex-1 rounded-full">
                {t('cancel')}
              </Button>
              <Button onClick={handleLongPeriodSubmit} disabled={lpSubmitting || !lpForm.start_date || !lpForm.end_date}
                data-testid="lp-submit-btn" className="flex-1 btn-primary">
                {lpSubmitting ? <Loader2 className="w-4 h-4 animate-spin" /> : t('sendRequest')}
              </Button>
            </div>
          </div>

          {/* My requests */}
          {myLpRequests.length > 0 && (
            <div className="mt-4 pt-4 border-t">
              <p className="text-sm font-bold text-slate-700 mb-2">{t('myRequests')}</p>
              <div className="space-y-2 max-h-40 overflow-y-auto">
                {myLpRequests.map(r => (
                  <div key={r.id} className={`text-xs p-2 rounded-lg ${
                    r.status === 'pending' ? 'bg-amber-50 text-amber-700' :
                    r.status === 'approved' ? 'bg-emerald-50 text-emerald-700' :
                    'bg-rose-50 text-rose-700'
                  }`} data-testid={`my-lp-${r.id}`}>
                    <span className="font-bold">{r.start_date} → {r.end_date}</span>
                    <span className="ml-2 px-1.5 py-0.5 rounded-full bg-white/60 font-bold">{t(r.status)}</span>
                    {r.admin_notes && <p className="mt-1 text-xs opacity-80">{r.admin_notes}</p>}
                  </div>
                ))}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
